import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Group, Menu, Modal, ScrollArea, Stack, Table, Text, TextInput, Textarea } from '@mantine/core';
import { tenderApi, type Bidder, type BidderStatus, type PackageDetail } from '../../api';
import { BIDDER_STATUS, formatAmount, formatDate, parseGermanNumber } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { useContextMenu } from '../../ui/ContextMenu';
import { parsePastedPrices } from './paste';
import { errorText, useTenderMutation } from './usePackage';

/**
 * The bidders of an award unit, in two views of the same list:
 * - "bieterkreis" (tendering): who is asked - company, contact, status - and
 *   the mail that sends the documents to all of them;
 * - "angebote" (award): who answered, on which terms, the offer total and rank,
 *   and the way into entering prices.
 */
export function BiddersPanel({ projectId, detail, canEdit, mode = 'angebote' }: { projectId: string; detail: PackageDetail; canEdit: boolean; mode?: 'bieterkreis' | 'angebote' }): JSX.Element {
  const offers = mode === 'angebote';
  const packageId = detail.package.id;
  const [company, setCompany] = useState('');
  const [priceFor, setPriceFor] = useState<Bidder | null>(null);
  const add = useTenderMutation(projectId, packageId, () => tenderApi.addBidder(projectId, packageId, { company: company.trim() }));
  const update = useTenderMutation(projectId, packageId, ({ id, ...body }: { id: string } & Parameters<typeof tenderApi.updateBidder>[3]) => tenderApi.updateBidder(projectId, packageId, id, body));
  const remove = useTenderMutation(projectId, packageId, (id: string) => tenderApi.removeBidder(projectId, packageId, id));
  const results = new Map(detail.evaluation.bidders.map((r) => [r.bidderId, r]));
  const error = add.error ?? update.error ?? remove.error;
  const positions = detail.items.filter((i) => i.kind === 'position').length;

  const context = useContextMenu<Bidder>((b, close) => (
    <>
      <Menu.Label>{b.company}</Menu.Label>
      {canEdit && offers && <Menu.Item onClick={() => { close(); setPriceFor(b); }}>Preise erfassen …</Menu.Item>}
      {b.email && <Menu.Item component="a" href={`mailto:${b.email}?subject=${encodeURIComponent(`${detail.package.code} ${detail.package.name}`)}`} onClick={close}>E-Mail schreiben</Menu.Item>}
      {canEdit && (
        <>
          <Menu.Divider />
          <Menu.Label>Status</Menu.Label>
          {(Object.keys(BIDDER_STATUS) as BidderStatus[]).map((s) => (
            <Menu.Item key={s} onClick={() => { close(); update.mutate({ id: b.id, status: s }); }} fw={b.status === s ? 600 : undefined}>{BIDDER_STATUS[s].label}</Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item color="red" onClick={() => { close(); remove.mutate(b.id); }}>Bietende Firma entfernen</Menu.Item>
        </>
      )}
    </>
  ));

  const subject = encodeURIComponent(`${detail.package.code} ${detail.package.name}`);
  const withMail = detail.bidders.filter((b) => b.email && b.status !== 'declined' && b.status !== 'excluded');

  return (
    <Stack gap="sm">
      {error && <Alert color="red">{errorText(error)}</Alert>}
      {!offers && withMail.length > 0 && (
        <Group justify="flex-end">
          <Button size="xs" variant="light" component="a" leftSection={<span aria-hidden>✉</span>}
            href={`mailto:?bcc=${withMail.map((b) => b.email).join(',')}&subject=${subject}`}
            title="Öffnet das eigene Mailprogramm, alle Adressen in Blindkopie">
            E-Mail an den Bieterkreis ({withMail.length})
          </Button>
        </Group>
      )}
      {detail.bidders.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">{offers ? 'Noch keine Bietenden – im Reiter Ausschreibung › Bieterkreis hinzufügen.' : 'Noch keine Bietenden.'}</Text>
      ) : (
        <Table.ScrollContainer minWidth={offers ? 820 : 640}>
          <Table verticalSpacing={5} highlightOnHover className="pst-tabular">
            <Table.Thead>
              <Table.Tr>
                {canEdit && <Table.Th w={30} />}
                <Table.Th>Bietende Firma</Table.Th>
                <Table.Th w={180}>Status</Table.Th>
                {offers && (
                  <>
                    <Table.Th w={90} ta="right">Nachlass (%)</Table.Th>
                    <Table.Th w={90} ta="right">Skonto (%)</Table.Th>
                    <Table.Th w={80} ta="right">Tage</Table.Th>
                    <Table.Th w={140} ta="right" title="nach Nachlass">Angebot netto (€)</Table.Th>
                    <Table.Th w={60} ta="right">Rang</Table.Th>
                  </>
                )}
                {!offers && <Table.Th w={60} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.bidders.map((b) => {
                const r = results.get(b.id)!;
                const status = BIDDER_STATUS[b.status];
                return (
                  <Table.Tr key={b.id} onContextMenu={(e) => context.open(e, b)}>
                    {canEdit && <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Bietende Firma entfernen" title="Bietende Firma entfernen" onClick={() => remove.mutate(b.id)}>✕</ActionIcon></Table.Td>}
                    <Table.Td>
                      <EditableText value={b.company} bold onCommit={(next) => canEdit && update.mutate({ id: b.id, company: next })} />
                      <Group gap={6} wrap="nowrap">
                        <EditableText value={b.contact ?? ''} size="xs" dimmed allowEmpty placeholder="Ansprechperson" onCommit={(next) => canEdit && update.mutate({ id: b.id, contact: next || null })} />
                        <EditableText value={b.email ?? ''} size="xs" dimmed allowEmpty placeholder="E-Mail" onCommit={(next) => canEdit && update.mutate({ id: b.id, email: next || null })} />
                      </Group>
                      {/* In the offers view the way into the prices sits with the company - no extra column to scroll to. */}
                      {offers && canEdit && (
                        <Button size="compact-xs" variant="light" mt={4} onClick={() => setPriceFor(b)} disabled={positions === 0}>Preise erfassen</Button>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Menu withinPortal position="bottom-start">
                        <Menu.Target>
                          <Badge component="button" variant="light" color={status.color} style={{ cursor: canEdit ? 'pointer' : undefined, textTransform: 'none' }} disabled={!canEdit}>{status.label}</Badge>
                        </Menu.Target>
                        {canEdit && (
                          <Menu.Dropdown>
                            {(Object.keys(BIDDER_STATUS) as BidderStatus[]).map((s) => (
                              <Menu.Item key={s} onClick={() => update.mutate({ id: b.id, status: s, ...(s === 'submitted' && !b.submittedOn ? { submittedOn: new Date().toISOString().slice(0, 10) } : {}) })}>{BIDDER_STATUS[s].label}</Menu.Item>
                            ))}
                          </Menu.Dropdown>
                        )}
                      </Menu>
                      {b.submittedOn && <Text size="xs" c="dimmed">Eingang {formatDate(b.submittedOn)}</Text>}
                    </Table.Td>
                    {offers && (<>
                    <Table.Td><AmountCell value={b.discountPercent} readOnly={!canEdit} onCommit={(v) => update.mutate({ id: b.id, discountPercent: v ?? 0 })} /></Table.Td>
                    <Table.Td><AmountCell value={b.skontoPercent} readOnly={!canEdit} onCommit={(v) => update.mutate({ id: b.id, skontoPercent: v ?? 0 })} /></Table.Td>
                    <Table.Td><AmountCell value={b.skontoDays} digits={0} readOnly={!canEdit} onCommit={(v) => update.mutate({ id: b.id, skontoDays: v === null ? null : Math.round(v) })} /></Table.Td>
                    <Table.Td>
                      <AmountCell value={r.net > 0 ? r.netAfterDiscount : null} readOnly bold={r.rank === 1} />
                      {r.missing > 0 && r.net > 0 && <Text size="xs" c="orange.7" ta="right">{r.missing} von {positions} ohne Preis</Text>}
                    </Table.Td>
                    <Table.Td ta="right">{r.rank ? <Badge variant={r.rank === 1 ? 'filled' : 'light'} color={r.rank === 1 ? 'green' : 'gray'}>{r.rank}</Badge> : '—'}</Table.Td>
                    </>)}
                    {!offers && (
                      <Table.Td ta="right">
                        {b.email && <ActionIcon variant="light" component="a" href={`mailto:${b.email}?subject=${subject}`} aria-label={`E-Mail an ${b.company}`} title={`E-Mail an ${b.company}`}>✉</ActionIcon>}
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {canEdit && !offers && (
        <form onSubmit={(e) => { e.preventDefault(); if (company.trim()) add.mutate(undefined, { onSuccess: () => setCompany('') }); }}>
          <Group gap="xs" align="flex-end">
            <TextInput label="Bietende Firma hinzufügen" placeholder="Firma" value={company} onChange={(e) => setCompany(e.currentTarget.value)} style={{ flex: 1, maxWidth: 380 }} />
            <Button type="submit" variant="light" disabled={!company.trim()} loading={add.isPending}>Hinzufügen</Button>
          </Group>
        </form>
      )}
      <Text size="xs" c="dimmed">
        {offers
          ? 'Der Nachlass zählt für die Rangfolge, das Skonto nicht – es hängt von pünktlicher Zahlung ab. Abgesagte und ausgeschlossene Bietende bekommen keinen Rang.'
          : 'Der Bieterkreis: wer die Unterlagen bekommt. Angebote, Preise und Wertung folgen im Reiter Vergabe.'}
      </Text>
      {priceFor && <PriceModal projectId={projectId} detail={detail} bidder={priceFor} onClose={() => setPriceFor(null)} />}
      {context.menu}
    </Stack>
  );
}

/**
 * Entering one bidder's unit prices: position by position, or pasted from the
 * offer's spreadsheet. Nothing is saved until "Speichern".
 */
function PriceModal({ projectId, detail, bidder, onClose }: { projectId: string; detail: PackageDetail; bidder: Bidder; onClose: () => void }): JSX.Element {
  const packageId = detail.package.id;
  const positions = detail.items.filter((i) => i.kind === 'position');
  const current = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const c of detail.evaluation.items) m.set(c.itemId, c.unitPrices[bidder.id] ?? null);
    return m;
  }, [detail, bidder.id]);
  const [draft, setDraft] = useState<Map<string, string>>(new Map());
  const [paste, setPaste] = useState('');
  const [pasteInfo, setPasteInfo] = useState<string | null>(null);
  useEffect(() => {
    setDraft(new Map(positions.map((p) => [p.id, current.get(p.id) === null || current.get(p.id) === undefined ? '' : formatAmount(current.get(p.id)!)])));
  }, [bidder.id]);
  const save = useTenderMutation(projectId, packageId, (list: { itemId: string; unitPrice: number | null }[]) => tenderApi.setPrices(projectId, packageId, bidder.id, list));

  const parsedDraft = positions.map((p) => ({ item: p, raw: draft.get(p.id) ?? '', value: parseGermanNumber(draft.get(p.id) ?? '') }));
  const invalid = parsedDraft.filter((d) => d.value === undefined).length;
  const total = parsedDraft.reduce((s, d) => s + (typeof d.value === 'number' && (d.item.positionType === 'normal' || d.item.positionType === 'lump') ? Math.round((d.item.quantity ?? 0) * d.value * 100) / 100 : 0), 0);

  const applyPaste = (): void => {
    const { prices, unknown } = parsePastedPrices(paste, positions.map((p) => p.oz));
    const next = new Map(draft);
    for (const p of positions) if (prices.has(p.oz)) next.set(p.id, formatAmount(prices.get(p.oz)!));
    setDraft(next);
    setPasteInfo(`${prices.size} Preise übernommen${unknown.length ? `; unbekannte OZ: ${unknown.slice(0, 8).join(', ')}` : ''}.`);
    setPaste('');
  };

  return (
    <Modal opened onClose={onClose} title={`Preise: ${bidder.company}`} size="xl">
      <Stack gap="sm">
        <Group align="flex-end" gap="xs">
          <Textarea label="Aus dem Angebot einfügen" description="Zwei Spalten „OZ, EP“ – oder nur die EP-Spalte in der Reihenfolge des LV" autosize minRows={2} maxRows={6} value={paste} onChange={(e) => setPaste(e.currentTarget.value)} style={{ flex: 1 }} styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} />
          <Button variant="light" disabled={!paste.trim()} onClick={applyPaste}>Übernehmen</Button>
        </Group>
        {pasteInfo && <Text size="xs" c="dimmed">{pasteInfo}</Text>}
        <ScrollArea.Autosize mah="55vh" type="auto" offsetScrollbars>
          <Table verticalSpacing={2} className="pst-tabular">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={90}>OZ</Table.Th>
                <Table.Th>Kurztext</Table.Th>
                <Table.Th w={110} ta="right">Menge</Table.Th>
                <Table.Th w={140} ta="right">EP netto (€)</Table.Th>
                <Table.Th w={130} ta="right">GP netto (€)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {parsedDraft.map(({ item, raw, value }) => (
                <Table.Tr key={item.id}>
                  <Table.Td><Text size="sm" ff="monospace" c="dimmed">{item.oz}</Text></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1}>{item.shortText}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{item.quantity?.toLocaleString('de-DE')} {item.unit}</Text></Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={raw}
                      error={value === undefined}
                      inputMode="decimal"
                      styles={{ input: { textAlign: 'right' } }}
                      onChange={(e) => { const v = e.currentTarget.value; setDraft((d) => new Map(d).set(item.id, v)); }}
                      aria-label={`EP ${item.oz}`}
                    />
                  </Table.Td>
                  <Table.Td ta="right"><Text size="sm" c={item.positionType === 'normal' || item.positionType === 'lump' ? undefined : 'dimmed'}>{typeof value === 'number' ? formatAmount(Math.round((item.quantity ?? 0) * value * 100) / 100) : ''}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
        {save.isError && <Alert color="red">{errorText(save.error)}</Alert>}
        <Group justify="space-between">
          <Text size="sm">Summe netto vor Nachlass: <b>{formatAmount(total)} €</b>{invalid > 0 && <Text span c="red.7"> · {invalid} Eingaben nicht lesbar</Text>}</Text>
          <Group gap="xs">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button
              disabled={invalid > 0}
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  parsedDraft.filter((d) => d.value !== undefined && (d.value ?? null) !== (current.get(d.item.id) ?? null)).map((d) => ({ itemId: d.item.id, unitPrice: d.value as number | null })),
                  { onSuccess: onClose },
                )
              }
            >
              Speichern
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

